// client/src/modules/marketing/routes.tsx
import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { lazyWithRetry } from '../../lib/lazyWithRetry';

/** Redirect helper for old composer edit URLs */
function ComposerRedirect() {
  const { postId } = useParams<{ postId: string }>();
  return <Navigate to={`/marketing/content/composer/${postId}`} replace />;
}

// ── Active pages ────────────────────────────────────────────
const MarketingDashboardNew = lazyWithRetry(() => import('../../pages/Marketing/MarketingDashboardNew'));
const ContentPage = lazyWithRetry(() => import('../../pages/Marketing/ContentPage'));
const AIDraftWizard = lazyWithRetry(() => import('../../pages/Marketing/AIDraftWizard'));
const PostComposer = lazyWithRetry(() => import('../../pages/Marketing/PostComposer'));
const MediaRelations = lazyWithRetry(() => import('../../pages/Marketing/MediaRelations'));
const SEOConsolidated = lazyWithRetry(() => import('../../pages/Marketing/SEOConsolidated'));
const MarketingSettings = lazyWithRetry(() => import('../../pages/Marketing/MarketingSettings'));
const TestimonialsPage = lazyWithRetry(() => import('../../pages/Marketing/TestimonialsPage'));
const BriefingEditor = lazyWithRetry(() => import('../../pages/Marketing/BriefingEditor'));
const SharedDocumentView = lazyWithRetry(() => import('../../pages/Marketing/SharedDocumentView'));

/**
 * Marketing module routes — relative to /marketing prefix
 * 6-item nav: Dashboard, Content, PR, SEO, Testimonials, Settings
 */
export const routes: RouteObject[] = [
  // ── Primary routes ──────────────────────────────────────
  { index: true, element: <ProtectedRoute><MarketingDashboardNew /></ProtectedRoute> },
  { path: 'content', element: <ProtectedRoute><ContentPage /></ProtectedRoute> },
  { path: 'content/composer', element: <ProtectedRoute><AIDraftWizard /></ProtectedRoute> },
  { path: 'content/composer/:postId', element: <ProtectedRoute><PostComposer /></ProtectedRoute> },
  { path: 'content/briefings/:id/edit', element: <ProtectedRoute><BriefingEditor /></ProtectedRoute> },
  // The consumer for a share link. ProtectedRoute is load-bearing: the
  // callable needs a session, and without it a signed-out click would fail as
  // an opaque error instead of a sign-in prompt.
  { path: 'd/:shareId', element: <ProtectedRoute><SharedDocumentView /></ProtectedRoute> },
  { path: 'pr', element: <ProtectedRoute><MediaRelations /></ProtectedRoute> },
  { path: 'seo', element: <ProtectedRoute><SEOConsolidated /></ProtectedRoute> },
  { path: 'testimonials', element: <ProtectedRoute><TestimonialsPage /></ProtectedRoute> },
  { path: 'settings', element: <ProtectedRoute><MarketingSettings /></ProtectedRoute> },

  // ── Redirects (old routes → new) ────────────────────────
  // Monitor/Analytics → Dashboard
  { path: 'analytics', element: <Navigate to="/marketing" replace /> },
  { path: 'review', element: <Navigate to="/marketing/content" replace /> },
  { path: 'agent-activity', element: <Navigate to="/marketing/settings" replace /> },

  // Old content sub-routes → Content
  { path: 'content/library', element: <Navigate to="/marketing/content" replace /> },
  { path: 'content/ideas', element: <Navigate to="/marketing/content" replace /> },
  { path: 'content/media', element: <Navigate to="/marketing/settings" replace /> },
  { path: 'calendar', element: <Navigate to="/marketing/content" replace /> },
  { path: 'ideas', element: <Navigate to="/marketing/content" replace /> },
  { path: 'approvals', element: <Navigate to="/marketing/content" replace /> },
  { path: 'media', element: <Navigate to="/marketing/settings" replace /> },

  // Old composer routes
  { path: 'composer', element: <Navigate to="/marketing/content/composer" replace /> },
  { path: 'composer/:postId', element: <ProtectedRoute><ComposerRedirect /></ProtectedRoute> },

  // Old SEO sub-routes → consolidated SEO page
  { path: 'seo/keywords', element: <Navigate to="/marketing/seo" replace /> },
  { path: 'seo/gbp', element: <Navigate to="/marketing/seo" replace /> },
  { path: 'seo/citations', element: <Navigate to="/marketing/seo" replace /> },
  { path: 'seo/competitors', element: <Navigate to="/marketing/seo" replace /> },
  { path: 'seo/gaps', element: <Navigate to="/marketing/seo" replace /> },
  { path: 'seo/authority', element: <Navigate to="/marketing/seo" replace /> },
  { path: 'seo/geo', element: <Navigate to="/marketing/seo" replace /> },
  { path: 'seo/ai-surfaces', element: <Navigate to="/marketing/seo" replace /> },

  // Catch-all
  { path: '*', element: <Navigate to="/marketing" replace /> },
];
